import { z } from 'zod';
import { UuidSchema } from '../_shared/ids.js';
import { ArticleStatusSchema } from './enums.js';
/**
 * El módulo Audiencia: qué se mide del sitio público y qué se muestra en el panel.
 *
 * **El archivo se llama `audiencia` y no `analytics` a propósito**, igual que `campania` no se
 * llama `ad`. Los re-exports ESM del barrel hacen que el browser pida cada submódulo por su ruta,
 * y `/analytics.js` es regla de todas las listas de filtros: con un bloqueador instalado el pedido
 * se cancela y el panel queda en blanco. Por lo mismo la ingesta es `/senales` y la lectura
 * `/audiencia`, y el chunk del tablero no puede llamarse `analytics-*.js`.
 *
 * **De dónde sale cada cosa.** El registro de lecturas por nota ya existía (`lecturas.ts`) y no se
 * reescribe: este módulo lo ensancha. Las constantes que ya viven allá —el dwell, la deduplicación
 * del lector, el período de volcado— se importan de ahí y no se duplican acá.
 *
 * **Privacidad: acá tampoco hay ninguna persona.** Ninguna señal lleva identificador del lector.
 * El servidor deriva dispositivo del `User-Agent` y provincia de la IP —los dos datos que ya
 * recibe para descartar bots y deduplicar—, y **no persiste ninguno de los dos**: solo el
 * agregado. El grano mínimo de toda tabla es una hora o un día, nunca un evento.
 */
// ─────────────────────────────────────────────────────────────
// Constantes de la medición
// ─────────────────────────────────────────────────────────────
/**
 * Techo de segundos que puede sumar una nota por lectura: 20 minutos.
 *
 * Sin tope, una pestaña abierta y visible toda la tarde envenena el promedio del medio entero.
 * Veinte minutos y no diez porque una nota larga se lee en quince, y por eso mismo va **junto**
 * con el corte por inactividad de abajo: el tope solo se alcanza si la persona siguió leyendo.
 */
export const TIEMPO_EN_NOTA_TOPE_S = 1_200;
/**
 * Cuánto se espera sin scroll, puntero ni teclado antes de pausar el contador de tiempo.
 *
 * Es lo que hace honesto al tope de 20 minutos. Sin esto, "tiempo en nota" mide cuánto estuvo la
 * pestaña abierta en primer plano, que no es lo mismo que cuánto leyó nadie.
 */
export const TIEMPO_EN_NOTA_INACTIVIDAD_MS = 90_000;
/**
 * Cuántas señales entran en un lote. El navegador junta y manda de a poco, no una por evento.
 *
 * El tope es la primera línea contra el abuso: la ingesta es anónima y sin throttler por IP (los
 * avisos llegan todos desde la IP del server SSR), así que el límite tiene que estar en la forma
 * del pedido y en la cuenta por sesión, no en quién lo manda.
 */
export const SENALES_POR_LOTE_MAX = 20;
/**
 * Techo de señales que se le aceptan a una misma sesión dentro de una hora. Pasado esto se
 * descartan en silencio.
 *
 * Trescientas es muchísimo para una persona —son cinco páginas por minuto durante una hora— y
 * ridículamente poco para un script. Lo que no cubre es un atacante rotando sesiones, que es el
 * mismo límite conocido que ya tiene el registro de lecturas: esto es un tablero de redacción,
 * no contabilidad.
 */
export const SENALES_POR_SESION_HORA_MAX = 300;
/**
 * Inactividad que cierra una visita. Volver después de esto es una visita nueva.
 *
 * Treinta minutos es la convención de la industria y coincide con `LECTURA_DEDUP_MINUTOS`, lo que
 * evita tener dos relojes distintos diciendo cosas parecidas sobre el mismo lector.
 */
export const SESION_INACTIVIDAD_MINUTOS = 30;
/**
 * La ventana de "Ahora mismo".
 *
 * Coincide con la deduplicación del lector a propósito: dentro de la ventana cada lector cuenta
 * una vez por nota, así que el número es **lectores que abrieron una nota en los últimos 30
 * minutos** y no pestañas abiertas ni recargas. El panel lo rotula "leyendo", que promete
 * inmediatez y no simultaneidad; la definición completa va en el detalle en vivo.
 */
export const AHORA_MISMO_MINUTOS = 30;
/**
 * Cuánto se guardan los agregados **por hora**.
 *
 * Sube de los 30 días de `LECTURA_RETENCION_DIAS` porque el tablero ofrece rangos de 30 días con
 * comparación contra los 30 anteriores: con 30 de retención, la mitad de esa comparación no
 * existiría. Noventa cubre el rango más largo con grano horario más su período anterior, y sobra.
 *
 * Es también el plazo de la curva "lecturas por hora" del detalle de una nota: pasado esto la
 * nota conserva sus totales —que salen del acumulado, no de la curva— pero pierde el detalle
 * hora por hora. El diseño lo contempla con un caso propio.
 */
export const RETENCION_HORARIA_DIAS = 90;
/**
 * Cuánto se guardan los agregados **por día**: veinticinco meses.
 *
 * Veinticinco y no veinticuatro para que "este agosto contra el agosto pasado" siga entrando el
 * día que alguien lo pida a fin de mes. Son 365 filas por año y por medio: no hay nada que
 * optimizar acá.
 */
export const RETENCION_DIARIA_MESES = 25;
/**
 * La zona en la que cortan los días del tablero.
 *
 * Todo se acumula en hora UTC, como el registro de lecturas. Pero un tablero que dice "hoy"
 * cortando en UTC le muestra a una redacción de Patagonia un día que empezó a las 21 de ayer, y
 * el primer reflejo de cualquiera es pensar que los números están mal. Vive en `Tenant.timezone`
 * y esto es el default; la interfaz declara la zona para que nadie compare contra otra
 * herramienta y se desconcierte.
 */
export const ZONA_DEL_MEDIO_DEFAULT = 'America/Argentina/Buenos_Aires';
// ─────────────────────────────────────────────────────────────
// Dimensiones
// ─────────────────────────────────────────────────────────────
/**
 * Qué clase de página se vio. Cinco valores cerrados y ni uno más: cada valor nuevo es una fila
 * más por hora y por medio en `audience_page_stats`, y una pregunta que alguien tiene que querer
 * contestar.
 *
 * `BUSQUEDA` cuenta que el buscador se usó. **El término no se guarda**: es texto que escribió una
 * persona y puede traer un nombre o un teléfono, y guardarlo rompería el invariante de que en las
 * tablas de este módulo no hay nadie.
 */
export const TipoDePaginaSchema = z.enum(['PORTADA', 'SECCION', 'NOTA', 'BUSQUEDA', 'ESTATICA']);
/** Con qué se lee. Tres valores: el User-Agent no da para más sin empezar a inventar. */
export const DispositivoSchema = z.enum(['MOVIL', 'TABLET', 'ESCRITORIO']);
export const DISPOSITIVO_LABEL = {
    MOVIL: 'Teléfono',
    TABLET: 'Tablet',
    ESCRITORIO: 'Escritorio',
};
/**
 * Desde dónde se lee: las 24 jurisdicciones argentinas más dos salidas.
 *
 * **Es un conjunto cerrado y grueso a propósito.** Sale de la IP del pedido —que la API ya recibe
 * para deduplicar y sigue sin persistir— y se agrega por día a nivel del **medio, nunca de una
 * nota**: `(medio, día, provincia)` son 26 valores que no identifican a nadie, mientras que
 * `(nota, provincia, día) → 1` sería una tajada muy fina de una persona concreta.
 *
 * No hay ciudad, ni código postal, ni coordenadas, aunque el proveedor las ofrezca.
 */
export const ProvinciaSchema = z.enum([
    'CABA',
    'BUENOS_AIRES',
    'CATAMARCA',
    'CHACO',
    'CHUBUT',
    'CORDOBA',
    'CORRIENTES',
    'ENTRE_RIOS',
    'FORMOSA',
    'JUJUY',
    'LA_PAMPA',
    'LA_RIOJA',
    'MENDOZA',
    'MISIONES',
    'NEUQUEN',
    'RIO_NEGRO',
    'SALTA',
    'SAN_JUAN',
    'SAN_LUIS',
    'SANTA_CRUZ',
    'SANTA_FE',
    'SANTIAGO_DEL_ESTERO',
    'TIERRA_DEL_FUEGO',
    'TUCUMAN',
    /** Cualquier IP que resuelva fuera de Argentina. No se desagrega por país. */
    'OTRO_PAIS',
    /** Sin resolver: `GEO_KIND=none`, IP privada, o el proveedor no la conoce. */
    'DESCONOCIDA',
]);
export const PROVINCIA_LABEL = {
    CABA: 'Ciudad de Buenos Aires',
    BUENOS_AIRES: 'Buenos Aires',
    CATAMARCA: 'Catamarca',
    CHACO: 'Chaco',
    CHUBUT: 'Chubut',
    CORDOBA: 'Córdoba',
    CORRIENTES: 'Corrientes',
    ENTRE_RIOS: 'Entre Ríos',
    FORMOSA: 'Formosa',
    JUJUY: 'Jujuy',
    LA_PAMPA: 'La Pampa',
    LA_RIOJA: 'La Rioja',
    MENDOZA: 'Mendoza',
    MISIONES: 'Misiones',
    NEUQUEN: 'Neuquén',
    RIO_NEGRO: 'Río Negro',
    SALTA: 'Salta',
    SAN_JUAN: 'San Juan',
    SAN_LUIS: 'San Luis',
    SANTA_CRUZ: 'Santa Cruz',
    SANTA_FE: 'Santa Fe',
    SANTIAGO_DEL_ESTERO: 'Santiago del Estero',
    TIERRA_DEL_FUEGO: 'Tierra del Fuego',
    TUCUMAN: 'Tucumán',
    OTRO_PAIS: 'Fuera del país',
    DESCONOCIDA: 'Sin determinar',
};
// ─────────────────────────────────────────────────────────────
// El catálogo de señales
// ─────────────────────────────────────────────────────────────
/**
 * Lo que el navegador del lector le puede contar al servidor. **El catálogo es cerrado**: una
 * señal que no está acá es un 400, no un evento que se guarda por las dudas.
 *
 * Cinco y no más. Cada una existe porque hay una pantalla que la muestra — el módulo no mide
 * "por si algún día sirve", que es como se llega a una base llena de dimensiones que nadie
 * consultó nunca. Lo que **no** está y se evaluó: procedencia (de dónde venía el lector),
 * compartidos y clics a enlaces externos.
 *
 * **Ninguna lleva datos del lector.** Ni id, ni sesión, ni IP, ni referer: quién es lo resuelve el
 * servidor con lo que ya tiene del pedido, y solo para deduplicar y agregar.
 */
export const SENALES = [
    'pagina_vista',
    'nota_leida',
    'nota_terminada',
    'tiempo_en_nota',
    'aviso_visto',
    'aviso_clic',
];
/** Se vio una página. La única señal que no habla de una nota en particular. */
export const SenalPaginaVista = z
    .object({
    tipo: z.literal('pagina_vista'),
    pagina: TipoDePaginaSchema,
    /**
     * La nota o la sección que se vio, cuando la página es una de las dos. El servidor lo valida
     * contra la base: un id inventado no cuenta.
     */
    entidadId: UuidSchema.nullish(),
    /**
     * `true` si esta vista abre una visita nueva. Lo decide el navegador con su propia marca de
     * sesión, y el servidor lo confirma contra la suya: sin esa confirmación, cualquiera podría
     * inflar "visitas" mandando el flag en cada vista.
     */
    abreVisita: z.boolean(),
})
    .strict();
/**
 * Alguien leyó una nota: estuvo `LECTURA_DWELL_MS` a la vista.
 *
 * **Es la señal que hoy es `POST /public/lecturas/:id`.** Cuando la ingesta unificada tenga
 * rodaje, el handler de esta señal llama al mismo `LecturasService.registrar` y el endpoint viejo
 * se retira: "Más leídas" del sitio público sigue saliendo de la misma cuenta, no de una segunda.
 */
export const SenalNotaLeida = z
    .object({ tipo: z.literal('nota_leida'), articleId: UuidSchema })
    .strict();
/** Llegó al final del cuerpo de la nota (≥ 90 % del scroll), con el dwell ya cumplido. */
export const SenalNotaTerminada = z
    .object({ tipo: z.literal('nota_terminada'), articleId: UuidSchema })
    .strict();
/** Cuánto estuvo la nota a la vista **y activa**. Se manda al irse. */
export const SenalTiempoEnNota = z
    .object({
    tipo: z.literal('tiempo_en_nota'),
    articleId: UuidSchema,
    /**
     * Segundos visibles y activos. El cliente ya aplica el tope y el corte por inactividad, y el
     * servidor **vuelve a aplicar el tope**: el cliente es de quien lo lee, no nuestro.
     */
    segundos: z.number().int().min(1).max(TIEMPO_EN_NOTA_TOPE_S),
})
    .strict();
/** Un aviso estuvo a la vista: ≥ 50 % del área por 1 segundo. Solo con el módulo de Publicidad. */
export const SenalAvisoVisto = z
    .object({ tipo: z.literal('aviso_visto'), adId: UuidSchema })
    .strict();
/** Le hicieron clic a un aviso. */
export const SenalAvisoClic = z
    .object({ tipo: z.literal('aviso_clic'), adId: UuidSchema })
    .strict();
export const Senal = z.discriminatedUnion('tipo', [
    SenalPaginaVista,
    SenalNotaLeida,
    SenalNotaTerminada,
    SenalTiempoEnNota,
    SenalAvisoVisto,
    SenalAvisoClic,
]);
/**
 * El cuerpo de `POST /public/senales`.
 *
 * Va en lote porque una lectura de nota genera tres señales (vista, leída, tiempo) y mandarlas
 * sueltas serían tres pedidos por nota. La respuesta es **siempre 204**, pase lo que pase adentro:
 * el navegador no tiene nada que hacer con la diferencia, y un 404 le contaría a cualquiera qué
 * ids existen en el medio.
 */
export const LoteDeSenales = z
    .object({ senales: z.array(Senal).min(1).max(SENALES_POR_LOTE_MAX) })
    .strict();
// ─────────────────────────────────────────────────────────────
// Rangos: qué período mira el tablero
// ─────────────────────────────────────────────────────────────
/**
 * Los cuatro rangos del selector. Cada uno se compara contra el período anterior de igual largo.
 *
 * `HOY` y `D7` se dibujan por hora; `D30` y `M12` por día. Es lo que decide de qué tabla sale la
 * serie —la horaria se poda a los 90 días y la diaria vive 25 meses— y por eso la granularidad no
 * es una preferencia de la pantalla: es una consecuencia del dato.
 */
export const RangoSchema = z.enum(['HOY', 'D7', 'D30', 'M12']);
export const RANGO_LABEL = {
    HOY: 'Hoy',
    D7: '7 días',
    D30: '30 días',
    M12: '12 meses',
};
export const GranularidadSchema = z.enum(['HORA', 'DIA', 'MES']);
export const GRANULARIDAD_DEL_RANGO = {
    HOY: 'HORA',
    D7: 'HORA',
    D30: 'DIA',
    M12: 'MES',
};
/** Cuántos días cubre cada rango, contando el día en curso. `M12` se resuelve por meses. */
export const DIAS_DEL_RANGO = {
    HOY: 1,
    D7: 7,
    D30: 30,
};
// ─────────────────────────────────────────────────────────────
// Días en la zona del medio
// ─────────────────────────────────────────────────────────────
/**
 * El corrimiento de una zona respecto de UTC, en milisegundos, **en un instante dado**.
 *
 * Se calcula con `Intl` y no con un offset fijo aunque hoy Argentina no tenga horario de verano:
 * el default es una zona, no un número, y el día que un medio esté en otra —o que vuelva el
 * horario de verano, que ya pasó tres veces en este país— un `-3` hardcodeado corre todos los
 * días del tablero sin que falle nada visible.
 */
function corrimientoDeZona(instante, zona) {
    const formato = new Intl.DateTimeFormat('en-US', {
        timeZone: zona,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    const partes = {};
    for (const parte of formato.formatToParts(instante)) {
        if (parte.type !== 'literal')
            partes[parte.type] = Number(parte.value);
    }
    // `hour` puede venir 24 en el cambio de día con hour12:false; Date.UTC lo normaliza igual.
    const comoSiFueraUtc = Date.UTC(partes['year'] ?? 1970, (partes['month'] ?? 1) - 1, partes['day'] ?? 1, partes['hour'] ?? 0, partes['minute'] ?? 0, partes['second'] ?? 0);
    return comoSiFueraUtc - instante.getTime();
}
/** El día calendario de un instante en la zona del medio, como `2026-08-22`. */
export function diaEnZona(instante, zona = ZONA_DEL_MEDIO_DEFAULT) {
    const corrido = new Date(instante.getTime() + corrimientoDeZona(instante, zona));
    return corrido.toISOString().slice(0, 10);
}
/**
 * El instante UTC en el que empieza (00:00) un día calendario de la zona del medio.
 *
 * La segunda pasada no es de más: el corrimiento se mide en un instante, y si ese instante cae
 * del otro lado de un cambio de horario, el primer cálculo da una hora corrida. Volver a medir
 * sobre el resultado y corregir converge, que es la forma barata de hacer esto sin una biblioteca
 * de zonas horarias entera.
 */
export function inicioDelDiaEnZona(dia, zona = ZONA_DEL_MEDIO_DEFAULT) {
    const [anio, mes, diaDelMes] = dia.split('-').map(Number);
    const medianocheUtc = Date.UTC(anio, mes - 1, diaDelMes);
    const primera = new Date(medianocheUtc - corrimientoDeZona(new Date(medianocheUtc), zona));
    return new Date(medianocheUtc - corrimientoDeZona(primera, zona));
}
/** Suma días a un día calendario, sin salir del calendario (no hay horas de por medio). */
export function sumarDias(dia, dias) {
    const [anio, mes, diaDelMes] = dia.split('-').map(Number);
    return new Date(Date.UTC(anio, mes - 1, diaDelMes + dias)).toISOString().slice(0, 10);
}
/** Suma meses a un día calendario, anclando al día 1: solo se usa para el rango de 12 meses. */
export function sumarMeses(dia, meses) {
    const [anio, mes] = dia.split('-').map(Number);
    return new Date(Date.UTC(anio, mes - 1 + meses, 1)).toISOString().slice(0, 10);
}
/**
 * Los días que cubre un rango, contando hacia atrás desde hoy e **incluyendo el día en curso**.
 *
 * Que el día en curso entre es lo que hace que "7 días" sea "esta semana y cómo venimos" y no
 * "la semana que terminó ayer", que es lo que una redacción no quiere mirar.
 */
export function ventanaDelRango(rango, ahora, zona = ZONA_DEL_MEDIO_DEFAULT) {
    const hoy = diaEnZona(ahora, zona);
    if (rango === 'M12')
        return { desde: sumarMeses(hoy, -11), hasta: hoy };
    return { desde: sumarDias(hoy, -(DIAS_DEL_RANGO[rango] - 1)), hasta: hoy };
}
/**
 * La ventana anterior de igual largo, que es contra la que se compara todo el tablero.
 *
 * Pegada y sin solaparse: si el rango es del 14 al 20, la anterior es del 7 al 13. Un rango que se
 * solapa con el suyo anterior compara una semana consigo misma y siempre da que se creció.
 */
export function ventanaAnterior(actual, rango) {
    if (rango === 'M12')
        return { desde: sumarMeses(actual.desde, -12), hasta: sumarMeses(actual.hasta, -12) };
    const dias = DIAS_DEL_RANGO[rango];
    return { desde: sumarDias(actual.desde, -dias), hasta: sumarDias(actual.hasta, -dias) };
}
// ─────────────────────────────────────────────────────────────
// Lo que el tablero lee
// ─────────────────────────────────────────────────────────────
/**
 * Un número con su comparación. `anterior` es `null` cuando el período previo cae antes de que
 * hubiera medición: ahí el panel escribe "sin comparación" y **no dibuja la serie fantasma**, que
 * en cero se leería como una caída.
 */
export const IndicadorComparado = z
    .object({
    valor: z.number(),
    anterior: z.number().nullable(),
    /** Variación relativa, ya calculada. `null` si no hay con qué comparar o si el previo es 0. */
    delta: z.number().nullable(),
})
    .strict();
/**
 * Un punto de la serie. `anterior` permite dibujar la comparación como forma y no como número.
 *
 * `sinMedicion` distingue **un período sin datos de uno con cero lecturas**, que es la diferencia
 * entre pintar la zona gris del gráfico y mentir. Lo puede afirmar porque el volcado diario
 * escribe la fila del día aunque no haya habido tráfico: sin esa fila en cero, "no medimos" y "no
 * vino nadie" serían indistinguibles.
 */
export const PuntoDeSerie = z
    .object({
    /** Inicio del bucket: hora ISO si la granularidad es `HORA`, si no el día (`2026-08-14`). */
    instante: z.string(),
    valor: z.number().int(),
    anterior: z.number().int().nullable(),
    sinMedicion: z.boolean(),
})
    .strict();
/** Una fila de las listas de proporción (secciones, provincias, dispositivos). */
export const FilaDeProporcion = z
    .object({ clave: z.string(), etiqueta: z.string(), valor: z.number().int() })
    .strict();
/**
 * Lectores únicos de **un** día, con su margen.
 *
 * Nunca aparece como total de un rango: el conteo es un HyperLogLog por día y dos sketches
 * distintos no se suman sin contar dos veces a la misma persona. El panel muestra el margen al
 * lado para que el número no se lea como exacto.
 */
export const UnicosDelDia = z
    .object({ dia: z.string(), valor: z.number().int(), margenRelativo: z.number() })
    .strict();
export const NotaEnRanking = z
    .object({
    articleId: UuidSchema,
    titulo: z.string(),
    categoria: z.string().nullable(),
    lecturas: z.number().int(),
})
    .strict();
/**
 * Si la medición está viva, y desde cuándo.
 *
 * **Existe porque una ingesta rota y un medio recién encendido se veían exactamente igual**: los dos
 * daban cero en todo, y la pantalla vacía decía "la medición está activa" en los dos casos. Es lo
 * que hizo que tres defectos silenciosos pudieran durar indefinidamente.
 *
 * No es un dato de audiencia y no se muestra como tal: sirve para elegir qué dice la pantalla
 * cuando no hay nada que mostrar.
 */
export const EstadoDeIngesta = z
    .object({
    /** Instante ISO del último lote que llegó, o `null` si no hay memoria de ninguno. */
    ultimaSenal: z.string().nullable(),
    /** Lotes recibidos en la hora en curso. */
    senalesEnLaHora: z.number().int(),
})
    .strict();
/**
 * Cuánto puede pasar sin una sola señal antes de que sea un problema y no una noche tranquila.
 *
 * Un día entero: un medio sin una visita en 24 horas o no tiene lectores o no está midiendo, y las
 * dos cosas merecen que la pantalla lo diga en vez de mostrar un cero tranquilizador.
 */
export const INGESTA_MUDA_HORAS = 24;
/** Lo que contesta la pestaña Resumen: ¿estamos creciendo? */
export const ResumenDeAudiencia = z
    .object({
    rango: RangoSchema,
    ventana: z.object({ desde: z.string(), hasta: z.string() }).strict(),
    /** La zona en la que cortan los días, para que la interfaz pueda declararla. */
    zona: z.string(),
    granularidad: GranularidadSchema,
    /**
     * Vistas de página **de una nota**: cuántas veces se abrió una nota.
     *
     * No es lo mismo que `lecturas`, y la diferencia es la que hace útil al par: acá se cuenta cada
     * vez que la página de una nota se cargó, sin deduplicar; `lecturas` cuenta las que superaron el
     * umbral de interacción, una sola vez por lector y nota. Lo que queda en el medio son las notas
     * que se abrieron y se abandonaron en el título.
     *
     * Deja afuera portada, sección, búsqueda y estáticas, que en el tráfico de referencia son
     * la mitad de las vistas del sitio.
     */
    notasAbiertas: IndicadorComparado,
    visitas: IndicadorComparado,
    notasPorVisita: IndicadorComparado,
    lecturas: IndicadorComparado,
    /**
     * Cuántas notas se publicaron en el rango.
     *
     * **Es el único número del resumen que no habla de la audiencia sino de la redacción**, y está
     * justamente por eso: cuando las vistas caen un 10 %, la primera pregunta es si cayó el interés
     * o si esa semana se publicó la mitad. Sin este dato el tablero no puede contestarla.
     *
     * Cuenta las notas **que hoy están publicadas** y cuya fecha de publicación cae en el rango, así
     * que una nota archivada después deja de contar hacia atrás. Es la lectura correcta —el archivo
     * es lo que el medio sostiene, no lo que alguna vez subió— y además es la única que se puede
     * afirmar sin guardar un historial de estados.
     */
    notasPublicadas: IndicadorComparado,
    llegaAlFinal: IndicadorComparado,
    tiempoMedioS: IndicadorComparado,
    /** Para saber si un cero es "no vino nadie" o "no estamos midiendo". Ver `EstadoDeIngesta`. */
    ingesta: EstadoDeIngesta,
    serie: z.array(PuntoDeSerie),
    secciones: z.array(FilaDeProporcion),
    provincias: z.array(FilaDeProporcion),
    topNotas: z.array(NotaEnRanking),
    /**
     * Si el medio tiene **alguna** historia medida, sin decir desde cuándo.
     *
     * Es lo único que separa los dos estados vacíos del tablero: con `false`, el módulo se acaba de
     * encender y todavía no hay nada —un estado tranquilo, que explica que la medición está activa—;
     * con `true` y el rango en cero, en este rango no hubo nada pero en otros sí, y lo que
     * corresponde es invitar a mover el selector. Sin esta bandera los dos se ven igual, y el
     * primero —que es justo el que ve un cliente recién encendido— parece un error.
     *
     * Booleano y no fecha a propósito: alcanza para elegir el estado y no expone nada más.
     */
    hayHistoria: z.boolean(),
})
    .strict();
/** El número en vivo de la barra superior, y las tres notas que lo componen. */
export const AhoraMismo = z
    .object({
    lectores: z.number().int(),
    notas: z.array(NotaEnRanking.pick({ articleId: true, titulo: true }).extend({
        lectores: z.number().int(),
    })),
    ventanaMinutos: z.number().int(),
})
    .strict();
export const FilaDeNota = z
    .object({
    articleId: UuidSchema,
    titulo: z.string(),
    categoria: z.string().nullable(),
    /** Se muestra con su chip: una nota archivada que tuvo lecturas sigue en el ranking. */
    estado: ArticleStatusSchema,
    publicadaEn: z.string().datetime().nullable(),
    lecturas: z.number().int(),
    /** Proporción 0–1. `null` si la nota no tiene lecturas en el rango. */
    llegaAlFinal: z.number().nullable(),
    tiempoMedioS: z.number().int().nullable(),
})
    .strict();
export const ORDEN_DE_NOTAS = ['lecturas', 'llegaAlFinal', 'tiempoMedioS', 'publicadaEn'];
export const OrdenDeNotasSchema = z.enum(ORDEN_DE_NOTAS);
/** La tabla pagina de a 25: con orden por columna se busca el puesto, no una fila puntual. */
export const NOTAS_POR_PAGINA = 25;
/**
 * El detalle de una nota. `curva` tiene tres formas y el diseño dibuja las tres distinto:
 * por hora mientras la nota es reciente, por día cuando la poda se llevó el detalle horario, y
 * nada cuando la nota no tiene una sola lectura registrada.
 */
export const CurvaDeNota = z.discriminatedUnion('grano', [
    z.object({ grano: z.literal('HORA'), puntos: z.array(PuntoDeSerie) }).strict(),
    z.object({ grano: z.literal('DIA'), puntos: z.array(PuntoDeSerie) }).strict(),
    z.object({ grano: z.literal('NINGUNO') }).strict(),
]);
export const DetalleDeNota = z
    .object({
    articleId: UuidSchema,
    titulo: z.string(),
    categoria: z.string().nullable(),
    estado: ArticleStatusSchema,
    publicadaEn: z.string().datetime().nullable(),
    lecturas: z.number().int(),
    llegaAlFinal: z.number().nullable(),
    tiempoMedioS: z.number().int().nullable(),
    /** "3.º de 50 notas con lecturas": el puesto sale del mismo orden que la tabla. */
    puesto: z.object({ posicion: z.number().int(), de: z.number().int() }).strict().nullable(),
    /** Contra qué se compara la barra de "cuánto se leyó": el promedio del medio en el rango. */
    promedioDelMedioLlegaAlFinal: z.number().nullable(),
    curva: CurvaDeNota,
    otrasDeLaCategoria: z.array(NotaEnRanking),
})
    .strict();
/** La pestaña Público: desde dónde y con qué nos leen. Nada de esto existe por nota. */
export const PublicoDeAudiencia = z
    .object({
    provincias: z.array(FilaDeProporcion),
    dispositivos: z.array(FilaDeProporcion),
    /** Días sueltos, no una suma: ver `UnicosDelDia`. */
    unicos: z.array(UnicosDelDia),
})
    .strict();
export const RendimientoDeAviso = z
    .object({
    adId: UuidSchema,
    nombre: z.string(),
    emplazamiento: z.string(),
    impresiones: z.number().int(),
    clics: z.number().int(),
    /** `null` con cero impresiones: la pantalla escribe "sin datos", no "0 %". */
    ctr: z.number().nullable(),
})
    .strict();
